import React, { useState, useEffect } from 'react';
import { 
  FiFileText, FiTarget, FiGrid, FiAward, FiShare2, 
  FiUser, FiGlobe, FiEye, FiPhone, FiSave, FiCheck, FiPlus, FiTrash2, FiEdit2
} from 'react-icons/fi';
import ImageUploadField from './ImageUploadField';
import AdminGovernance from './AdminGovernance';
import AdminContactInfo from './AdminContactInfo';
import AdminPartners from './AdminPartners';
import './AdminManage.css';

// Storage keys
const STORAGE_KEY_INSTITUTE = 'iamar_institute_content';

const DEFAULT_INSTITUTE_DATA = {
  // 1. Présentation de l'Institut
  presentation: {
    title: "PRÉSENTATION DE L'INSTITUT",
    leadText: "L'Institut Afro-Méditerranéen d'Analyse des Risques (IAMAR) est une plateforme internationale de recherche, de formation et d'expertise dédiée à l'étude des transformations contemporaines de la gouvernance sécuritaire.",
    missionDetails: "Implanté à Agadir, au carrefour des espaces africain, méditerranéen et euro-atlantique, IAMAR se positionne comme un acteur stratégique émergent, capable de produire des analyses à haute valeur ajoutée et de proposer des solutions opérationnelles adaptées aux enjeux complexes de sécurité et de développement.",
    logoUrl: ""
  },
  // 2. Missions Principales
  missions: [
    { id: 1, step: "01", title: "Recherche Appliquée", desc: "Produire une recherche scientifique de pointe." },
    { id: 2, step: "02", title: "Formation", desc: "Développer des programmes de formation exécutive." },
    { id: 3, step: "03", title: "Accompagnement", desc: "Soutenir les institutions dans leur transformation." },
    { id: 4, step: "04", title: "Dialogue", desc: "Créer des espaces de dialogue international." }
  ],
  // 3. Domaines d'Intervention
  domaines: [
    { id: 1, color: "blue", title: "Gouvernance sécuritaire et réforme des institutions" },
    { id: 2, color: "red", title: "Transformation numérique et cybersécurité" },
    { id: 3, color: "white", title: "Analyse des risques et intelligence stratégique" },
    { id: 4, color: "black", title: "Communication publique et dynamiques sociétales" }
  ],
  // 4. Le Fondateur
  founder: {
    name: "Dr Redouane EL MAJDOULI",
    role: "FONDATEUR IAMAR",
    subtitle: "Expert en Gouvernance Sécuritaire",
    quote: "Fort d'une expérience de plus de deux décennies au sein des institutions publiques, il développe une approche scientifique innovante articulant analyse stratégique, communication et technologies émergentes.",
    photoUrl: "",
    experienceTag: "20+ ANS D'EXPÉRIENCE",
    specialties: ["Analyse Stratégique", "Gouvernance", "Cyber-Sécurité"]
  },
  // 5. Ambition Internationale
  ambition: {
    title: "Ambition internationale",
    subtitle: "Trois piliers fondamentaux au service d'une vision globale.",
    pillars: [
      { id: 1, num: "01", color: "navy", title: "Hub Académique", desc: "Un pôle d'excellence pour la recherche et la formation avancée au carrefour des espaces africain et méditerranéen." },
      { id: 2, num: "02", color: "red", title: "Think Tank de Référence", desc: "Production d'analyses stratégiques à haute valeur ajoutée sur les mutations de la gouvernance sécuritaire." },
      { id: 3, num: "03", color: "dark", title: "Partenaire Stratégique", desc: "Développement de réseaux d'expertise et de coopération avec les acteurs majeurs de la sécurité mondiale." }
    ]
  },
  // 6. Vision Stratégique
  vision: {
    title: "VISION STRATÉGIQUE",
    statement: "Contribuer à l'émergence d'un modèle de gouvernance sécuritaire inclusif, innovant et résilient, capable de répondre aux défis complexes du XXIe siècle.",
    principles: [
      { id: 1, num: "01", title: "Sécurité centrée sur l'humain", desc: "Promotion d'approches participatives intégrant les acteurs non étatiques et les dynamiques locales." },
      { id: 2, num: "02", title: "Innovation numérique", desc: "Intégration des technologies émergentes (IA, big data, cybersécurité) dans les systèmes de sécurité." },
      { id: 3, num: "03", title: "Gouvernance éthique", desc: "Respect des droits fondamentaux, transparence institutionnelle et responsabilité publique." },
      { id: 4, num: "04", title: "Analyse systémique", desc: "Analyse croisée des risques sécuritaires, environnementaux, numériques et sociétaux." }
    ]
  }
};

export default function AdminInstitute() {
  const [activeSubTab, setActiveSubTab] = useState('presentation');
  const [data, setData] = useState(DEFAULT_INSTITUTE_DATA);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [specInput, setSpecInput] = useState('');

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_INSTITUTE);
      if (saved) {
        setData(JSON.parse(saved));
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  const handleSave = () => {
    localStorage.setItem(STORAGE_KEY_INSTITUTE, JSON.stringify(data));
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  const updateField = (section, field, value) => {
    setData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  // Sub tabs list exactly matching user specifications
  const subTabs = [
    { id: 'presentation', label: "Présentation de l'Institut", icon: <FiFileText /> },
    { id: 'missions', label: "Missions Principales", icon: <FiTarget /> },
    { id: 'domaines', label: "Domaines d'Intervention", icon: <FiGrid /> },
    { id: 'gouvernance', label: "Gouvernance", icon: <FiAward /> },
    { id: 'ecosysteme', label: "Écosystème Collaboratif", icon: <FiShare2 /> },
    { id: 'fondateur', label: "Le Fondateur", icon: <FiUser /> },
    { id: 'ambition', label: "Ambition Internationale", icon: <FiGlobe /> },
    { id: 'vision', label: "Vision Stratégique", icon: <FiEye /> },
    { id: 'contact', label: "Contact & Coordonnées", icon: <FiPhone /> }
  ];

  return (
    <div className="admin-manage-card" style={{ background: '#ffffff', borderRadius: 8, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
      {/* Top Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: 16, marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.4rem', color: '#1b1e4f', fontWeight: 700 }}>
            Gestion des Composants : Institut IAMAR
          </h2>
          <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '0.88rem' }}>
            Configurez et personnalisez toutes les sections affichées sur la page Institut
          </p>
        </div>

        {activeSubTab !== 'gouvernance' && activeSubTab !== 'contact' && activeSubTab !== 'ecosysteme' && (
          <button
            onClick={handleSave}
            style={{
              background: saveSuccess ? '#10b981' : '#1b1e4f',
              color: 'white',
              border: 'none',
              padding: '9px 20px',
              borderRadius: 6,
              fontWeight: 600,
              fontSize: 13,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
          >
            {saveSuccess ? <FiCheck size={16} /> : <FiSave size={16} />}
            {saveSuccess ? 'Enregistré avec succès !' : 'Enregistrer les modifications'}
          </button>
        )}
      </div>

      {/* Sub-tabs pills */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 24, padding: 6, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
        {subTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            style={{
              border: 'none',
              background: activeSubTab === tab.id ? '#1b1e4f' : 'transparent',
              color: activeSubTab === tab.id ? '#ffffff' : '#475569',
              padding: '8px 14px',
              borderRadius: 6,
              fontSize: 12.5,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              transition: 'all 0.15s ease'
            }}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* SUBTAB 1: Présentation de l'Institut */}
      {activeSubTab === 'presentation' && (
        <div style={{ maxWidth: 840 }}>
          <h3 style={{ fontSize: '1.15rem', color: '#1e293b', marginBottom: 16 }}>
            Présentation de l'Institut
          </h3>

          <ImageUploadField
            label="Logo / Emblème de l'Institut (Upload image)"
            value={data.presentation.logoUrl}
            onChange={(url) => updateField('presentation', 'logoUrl', url)}
            helpText="Glissez ou choisissez le logo officiel de l'Institut IAMAR"
          />

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 6, color: '#334155' }}>
              Titre de la section
            </label>
            <input
              type="text"
              value={data.presentation.title}
              onChange={(e) => updateField('presentation', 'title', e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 14 }}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 6, color: '#334155' }}>
              Texte d'accroche (Lead Text)
            </label>
            <textarea
              rows={3}
              value={data.presentation.leadText}
              onChange={(e) => updateField('presentation', 'leadText', e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 14 }}
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 6, color: '#334155' }}>
              Détails de la mission & Positionnement stratégique
            </label>
            <textarea
              rows={4}
              value={data.presentation.missionDetails}
              onChange={(e) => updateField('presentation', 'missionDetails', e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 14 }}
            />
          </div>
        </div>
      )}

      {/* SUBTAB 2: Missions Principales */}
      {activeSubTab === 'missions' && (
        <div>
          <h3 style={{ fontSize: '1.15rem', color: '#1e293b', marginBottom: 16 }}>
            Missions Principales (Étapes & Objectifs)
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
            {data.missions.map((mission, idx) => (
              <div key={mission.id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, background: '#f8fafc' }}>
                <span style={{ fontSize: 12, fontWeight: 700, background: '#e0e7ff', color: '#3730a3', padding: '2px 8px', borderRadius: 4 }}>
                  Étape {mission.step}
                </span>
                <div style={{ marginTop: 12, marginBottom: 10 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Titre de la mission</label>
                  <input
                    type="text"
                    value={mission.title}
                    onChange={(e) => {
                      const newM = [...data.missions];
                      newM[idx].title = e.target.value;
                      setData(prev => ({ ...prev, missions: newM }));
                    }}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Description</label>
                  <textarea
                    rows={2}
                    value={mission.desc}
                    onChange={(e) => {
                      const newM = [...data.missions];
                      newM[idx].desc = e.target.value;
                      setData(prev => ({ ...prev, missions: newM }));
                    }}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SUBTAB 3: Domaines d'Intervention */}
      {activeSubTab === 'domaines' && (
        <div>
          <h3 style={{ fontSize: '1.15rem', color: '#1e293b', marginBottom: 16 }}>
            Domaines d'Intervention
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
            {data.domaines.map((dom, idx) => (
              <div key={dom.id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, background: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#0369a1', background: '#e0f2fe', padding: '2px 8px', borderRadius: 4 }}>
                  Domaine #{dom.id} ({dom.color})
                </span>
                <div style={{ marginTop: 12 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Intitulé du domaine</label>
                  <input
                    type="text"
                    value={dom.title}
                    onChange={(e) => {
                      const newD = [...data.domaines];
                      newD[idx].title = e.target.value;
                      setData(prev => ({ ...prev, domaines: newD }));
                    }}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SUBTAB 4: Gouvernance */}
      {activeSubTab === 'gouvernance' && (
        <div>
          <div style={{ marginBottom: 16, padding: '10px 14px', background: '#f1f5f9', borderRadius: 6, fontSize: 13, color: '#475569' }}>
            Gestion des membres de la gouvernance, comités directeurs et pilotage stratégique de l'Institut.
          </div>
          <AdminGovernance />
        </div>
      )}

      {/* SUBTAB 5: Écosystème Collaboratif */}
      {activeSubTab === 'ecosysteme' && (
        <div>
          <div style={{ marginBottom: 16, padding: '10px 14px', background: '#f1f5f9', borderRadius: 6, fontSize: 13, color: '#475569' }}>
            Gestion des partenaires universitaires, institutionnels, organisations internationales et secteur privé.
          </div>
          <AdminPartners />
        </div>
      )}

      {/* SUBTAB 6: Le Fondateur */}
      {activeSubTab === 'fondateur' && (
        <div style={{ maxWidth: 840 }}>
          <h3 style={{ fontSize: '1.15rem', color: '#1e293b', marginBottom: 16 }}>
            Informations sur le Fondateur
          </h3>

          <ImageUploadField
            label="Photo officielle du Fondateur (Upload image)"
            value={data.founder.photoUrl}
            onChange={(url) => updateField('founder', 'photoUrl', url)}
            helpText="Importez un portrait officiel haute résolution du Fondateur"
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Nom complet</label>
              <input
                type="text"
                value={data.founder.name}
                onChange={(e) => updateField('founder', 'name', e.target.value)}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 14 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Titre / Rôle</label>
              <input
                type="text"
                value={data.founder.role}
                onChange={(e) => updateField('founder', 'role', e.target.value)}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 14 }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Sous-titre / Spécialisation</label>
              <input
                type="text"
                value={data.founder.subtitle}
                onChange={(e) => updateField('founder', 'subtitle', e.target.value)}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 14 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Badge Expérience</label>
              <input
                type="text"
                value={data.founder.experienceTag}
                onChange={(e) => updateField('founder', 'experienceTag', e.target.value)}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 14 }}
              />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Citation / Biographie introductive</label>
            <textarea
              rows={3}
              value={data.founder.quote}
              onChange={(e) => updateField('founder', 'quote', e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 14 }}
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Piliers de spécialité (Tags)</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
              {data.founder.specialties.map((spec, i) => (
                <span key={i} style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '4px 10px', borderRadius: 20, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {spec}
                  <button
                    type="button"
                    onClick={() => {
                      const newSpecs = data.founder.specialties.filter((_, idx) => idx !== i);
                      updateField('founder', 'specialties', newSpecs);
                    }}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 14 }}
                  >
                    &times;
                  </button>
                </span>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                placeholder="Ajouter une spécialité..."
                value={specInput}
                onChange={(e) => setSpecInput(e.target.value)}
                style={{ flex: 1, padding: '8px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
              />
              <button
                type="button"
                onClick={() => {
                  if (specInput.trim()) {
                    updateField('founder', 'specialties', [...data.founder.specialties, specInput.trim()]);
                    setSpecInput('');
                  }
                }}
                style={{ background: '#1b1e4f', color: 'white', border: 'none', padding: '8px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SUBTAB 7: Ambition Internationale */}
      {activeSubTab === 'ambition' && (
        <div>
          <h3 style={{ fontSize: '1.15rem', color: '#1e293b', marginBottom: 16 }}>
            Ambition Internationale (Piliers Stratégiques)
          </h3>
          <div style={{ marginBottom: 16, maxWidth: 600 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Sous-titre d'accroche</label>
            <input
              type="text"
              value={data.ambition.subtitle}
              onChange={(e) => updateField('ambition', 'subtitle', e.target.value)}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
            {data.ambition.pillars.map((pil, idx) => (
              <div key={pil.id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, background: '#f8fafc' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', background: '#fee2e2', padding: '2px 8px', borderRadius: 4 }}>
                  Pilier {pil.num}
                </span>
                <div style={{ marginTop: 12, marginBottom: 10 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Titre du pilier</label>
                  <input
                    type="text"
                    value={pil.title}
                    onChange={(e) => {
                      const newP = [...data.ambition.pillars];
                      newP[idx].title = e.target.value;
                      setData(prev => ({ ...prev, ambition: { ...prev.ambition, pillars: newP } }));
                    }}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Description</label>
                  <textarea
                    rows={3}
                    value={pil.desc}
                    onChange={(e) => {
                      const newP = [...data.ambition.pillars];
                      newP[idx].desc = e.target.value;
                      setData(prev => ({ ...prev, ambition: { ...prev.ambition, pillars: newP } }));
                    }}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SUBTAB 8: Vision Stratégique */}
      {activeSubTab === 'vision' && (
        <div style={{ maxWidth: 840 }}>
          <h3 style={{ fontSize: '1.15rem', color: '#1e293b', marginBottom: 16 }}>
            Vision Stratégique & Principes
          </h3>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Déclaration de la Vision Centrale</label>
            <textarea
              rows={3}
              value={data.vision.statement}
              onChange={(e) => updateField('vision', 'statement', e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 14 }}
            />
          </div>

          <h4 style={{ fontSize: '1rem', color: '#1e293b', marginBottom: 12 }}>Principes Fondamentaux</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
            {data.vision.principles.map((pr, idx) => (
              <div key={pr.id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, background: '#f8fafc' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#1b1e4f', background: '#e2e8f0', padding: '2px 8px', borderRadius: 4 }}>
                  Principe #{pr.num}
                </span>
                <div style={{ marginTop: 12, marginBottom: 10 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Titre du principe</label>
                  <input
                    type="text"
                    value={pr.title}
                    onChange={(e) => {
                      const newPr = [...data.vision.principles];
                      newPr[idx].title = e.target.value;
                      setData(prev => ({ ...prev, vision: { ...prev.vision, principles: newPr } }));
                    }}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Description</label>
                  <textarea
                    rows={2}
                    value={pr.desc}
                    onChange={(e) => {
                      const newPr = [...data.vision.principles];
                      newPr[idx].desc = e.target.value;
                      setData(prev => ({ ...prev, vision: { ...prev.vision, principles: newPr } }));
                    }}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SUBTAB 9: Contact */}
      {activeSubTab === 'contact' && (
        <div>
          <div style={{ marginBottom: 16, padding: '10px 14px', background: '#f1f5f9', borderRadius: 6, fontSize: 13, color: '#475569' }}>
            Gestion des coordonnées de contact de l'Institut (Adresse, Email, Téléphone, Horaires).
          </div>
          <AdminContactInfo />
        </div>
      )}
    </div>
  );
}
